import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import {
  Badge as AdmitCardIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import jsPDF from "jspdf";
import LoadingBar from "react-top-loading-bar";

// ─── Small formatting helpers (kept identical to the Result module) ───────
const MONTH_NAMES = [
  "",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const formatDueDate = (val) => {
  if (!val) return "--";
  const d = new Date(val);
  return isNaN(d.getTime()) ? "--" : d.toLocaleDateString();
};

const StudentAdmitCard = () => {
  // ── Page / list state ────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("warning");
  const [openMissingDialog, setOpenMissingDialog] = useState(false);

  // ── Session / exam selection ────────────────────────────────────────
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState("");
  const [examTypes, setExamTypes] = useState([]);
  const [selectedExamType, setSelectedExamType] = useState("");
  const [subExams, setSubExams] = useState([]);
  const [selectedSubExam, setSelectedSubExam] = useState("");
  const [isExamModeIndependent, setIsExamModeIndependent] = useState(0);
  const [isSubExamDisabled, setIsSubExamDisabled] = useState(false);

  // ── The logged-in student's own admit card record ──────────────────
  const [studentAdmitData, setStudentAdmitData] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // ── Fee-due restriction — no bypass for students ────────────────────
  const [isFeeCheckLoading, setIsFeeCheckLoading] = useState(false);
  const [dueDialogOpen, setDueDialogOpen] = useState(false);
  const [dueData, setDueData] = useState(null);

  const token = sessionStorage.getItem("token");
  const apiUrl = process.env.REACT_APP_BASE_URL;
  const classId = sessionStorage.getItem("classId");
  const sectionId = sessionStorage.getItem("sectionId");
  const loggedInStudentId = sessionStorage.getItem("employeeId");

  const showSnackbar = (message, severity = "warning") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  // ── Fetch financial years (sessions) — identical to Result module ──
  const fetchSessions = async () => {
    try {
      setLoadingBarProgress(30);
      const response = await fetch(`${apiUrl}/ClassPromotion/GetFinancialYear`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setSessions(data);

      const today = new Date();
      const currentYear = today.getFullYear();
      const nextYear = currentYear + 1;
      const financialYearStart = new Date(currentYear, 3, 1);
      const financialYearEnd = new Date(nextYear, 2, 31);

      let currentSessionString;
      if (today >= financialYearStart && today <= financialYearEnd) {
        currentSessionString = `${currentYear}-${nextYear}`;
      } else {
        currentSessionString = `${currentYear - 1}-${currentYear}`;
      }

      const currentSession = data.find(
        (item) => item.finanacialYear === currentSessionString
      );

      if (currentSession) {
        setSession(currentSession.financialYearID);
      } else if (data.length > 0) {
        setSession(data[0].financialYearID);
      }

      setLoadingBarProgress(100);
    } catch (error) {
      console.error("Error fetching financial years:", error);
      showSnackbar("Failed to fetch sessions. Please try again.", "error");
    }
  };

  // ── Fetch exam types ────────────────────────────────────────────────
  useEffect(() => {
    const fetchExamTypes = async () => {
      try {
        const url = `${apiUrl}/ExaminationCriteria/GetExaminationCriteria`;
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ examCategoryId: 0 }),
        });
        const data = await response.json();
        if (data && Array.isArray(data)) {
          setExamTypes(data);
        } else {
          showSnackbar("No exam types found.", "warning");
        }
      } catch (error) {
        console.error("Error fetching exam types:", error);
        showSnackbar("Failed to fetch exam types. Please try again.", "error");
      }
    };
    fetchExamTypes();
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Independent-mode exam handling (disables Sub Exam) ─────────────
  useEffect(() => {
    if (!selectedExamType) return;
    const selectedExam = examTypes.find(
      (exam) => exam.examCriteriaId === selectedExamType
    );
    if (selectedExam) {
      setIsExamModeIndependent(selectedExam.isExamModeIndependent);
      if (selectedExam.isExamModeIndependent === 1) {
        setIsSubExamDisabled(true);
        setSelectedSubExam("");
        setSubExams([]);
      } else {
        setIsSubExamDisabled(false);
      }
    }
  }, [selectedExamType, examTypes]);

  // ── Fetch sub exams for the selected exam type ─────────────────────
  useEffect(() => {
    if (!selectedExamType || isExamModeIndependent === 1) return;
    const fetchSubExams = async () => {
      try {
        const url = `${apiUrl}/Teacher/ddlExamSubCategory_Examination`;
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ examCategoryId: selectedExamType }),
        });
        const data = await response.json();
        if (data && Array.isArray(data)) {
          setSubExams(
            data.map((subExam) => ({
              id: subExam.examSubCategoryId,
              name: subExam.examSubCategory,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching sub exams:", error);
        showSnackbar("Failed to fetch sub-exams. Please try again.", "error");
      }
    };
    fetchSubExams();
  }, [selectedExamType, isExamModeIndependent]);

  // Clear previously fetched admit card whenever the filters change
  useEffect(() => {
    setStudentAdmitData(null);
    setHasSearched(false);
  }, [selectedExamType, selectedSubExam, session]);

  // ── Fetch the admit card list and pick out the logged-in student ───
  const handleGetAdmitDetails = async () => {
    if (
      !selectedExamType ||
      (!isSubExamDisabled && !selectedSubExam) ||
      !session
    ) {
      setOpenMissingDialog(true);
      return;
    }
    if (!classId || !sectionId) {
      showSnackbar("Class/Section information missing. Please re-login.", "error");
      return;
    }

    setIsLoading(true);
    setHasSearched(true);
    try {
      const response = await fetch(`${apiUrl}/AdmitCard/GetGeneratedAdmitcarAll`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          examCategoryId: selectedExamType,
          subExamId: isSubExamDisabled ? 0 : selectedSubExam,
          classId,
          sectionId,
          sessionId: session,
        }),
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();

      if (data && Array.isArray(data.data) && data.data.length > 0) {
        const own = data.data.find(
          (s) => String(s.studentId) === String(loggedInStudentId)
        );
        if (own) {
          setStudentAdmitData(own);
          showSnackbar("Admit card found. You can download it below.", "success");
        } else {
          setStudentAdmitData(null);
          showSnackbar(
            "No admit card found for you under the selected criteria.",
            "warning"
          );
        }
      } else {
        setStudentAdmitData(null);
        showSnackbar(
          "No admit card details found for the selected criteria.",
          "warning"
        );
      }
    } catch (error) {
      console.error("Error fetching admit card details:", error);
      showSnackbar("Failed to fetch admit card details. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // ── FEE CHECK — runs before admit card generation, hard block on dues ──
  //   canViewResult: true  → dues are clear, proceed to PDF generation.
  //   canViewResult: false → dues are pending. Show breakdown and STOP —
  //   there is no bypass on the student panel.
  const handleDownloadClick = async () => {
    if (!studentAdmitData) return;
    setIsFeeCheckLoading(true);
    try {
      const response = await fetch(`${apiUrl}/Result/StudentResultWithFeeCheck`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          studentId: studentAdmitData.studentId,
          sessionId: session,
          examTypeId: selectedExamType,
          subExamId: isSubExamDisabled ? 0 : selectedSubExam,
          subjectId: 0,
        }),
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();

      if (data.canViewResult) {
        handlePrint(studentAdmitData.studentId);
      } else {
        setDueData(data);
        setDueDialogOpen(true);
      }
    } catch (error) {
      console.error("Error checking fee status:", error);
      showSnackbar("Error checking fee status. Please try again.", "error");
    } finally {
      setIsFeeCheckLoading(false);
    }
  };

  const handleCloseDueDialog = () => {
    setDueDialogOpen(false);
    setDueData(null);
  };

  const handleMissingDialogClose = () => setOpenMissingDialog(false);

  // ── PDF GENERATION — same layout as the staff Admit Card module ────────
  const handlePrint = async (studentId) => {
    setIsPdfLoading(true);
    setLoadingBarProgress(30);
    try {
      const schoolName =
        sessionStorage.getItem("organizationName")?.replace(/['"]+/g, "") ||
        "IDEAL PUBLIC SCHOOL";
      const schoolAddress =
        sessionStorage.getItem("address")?.replace(/['"]+/g, "") ||
        "STREET NO 1 2 B- BLOCK SARUP VIHAR";
      const contactInfo =
        sessionStorage.getItem("phoneNo")?.replace(/['"]+/g, "") || "1234567890";

      const payload = {
        examTypeId: selectedExamType,
        subExamId: isSubExamDisabled ? 0 : selectedSubExam,
        classId,
        sectionId,
        sessionId: session,
        studentId,
      };

      setLoadingBarProgress(60);
      const response = await fetch(`${apiUrl}/AdmitCard/PrintAdmitCard`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.statusText}`);
      }

      const apiData = await response.json();
      if (!apiData || !apiData.data || !apiData.data.length) {
        throw new Error("No admit card data found.");
      }

      const admitCardData = apiData.data[0];
      const doc = new jsPDF();

      doc.setDrawColor("#000");
      doc.setLineWidth(0.5);
      doc.rect(10, 10, 192, 277);

      const headerColor = "#0B1F3D";
      const textColor = "#000";

      doc.setFont("Helvetica", "bold");
      doc.setFontSize(14);
      doc.setTextColor(headerColor);
      doc.text(schoolName, 105, 20, { align: "center" });
      doc.setFontSize(10);
      doc.setTextColor(textColor);
      doc.text(schoolAddress, 105, 28, { align: "center" });
      doc.text(contactInfo, 105, 34, { align: "center" });

      doc.setDrawColor("#000");
      doc.setLineWidth(0.2);
      doc.line(20, 38, 190, 38);

      doc.setFontSize(13);
      doc.setFont("Helvetica", "bold");
      doc.setTextColor("#333");
      doc.text(
        `${admitCardData.examType || "N/A"}${
          typeof admitCardData.subExam === "string" ? " - " + admitCardData.subExam : ""
        }`,
        105,
        50,
        { align: "center" }
      );

      doc.setDrawColor("#45969C");
      doc.setLineWidth(0.8);
      doc.line(55, 53, 155, 53);

      doc.setLineWidth(0.2);
      doc.setDrawColor("#000");

      doc.setFontSize(10);
      doc.setFont("Helvetica", "normal");

      const detailsTopMargin = 65;
      const studentDetails = [
        `NAME: ${admitCardData.studentName || "N/A"}`,
        `FATHER'S NAME: ${admitCardData.fathersName || "N/A"}`,
        `CLASS/GRADE: ${admitCardData.className || "N/A"}`,
        `SECTION: ${admitCardData.sectionName || "N/A"}`,
        `ROLL NUMBER: ${admitCardData.rollNo || "N/A"}`,
        `ADMISSION NO: ${admitCardData.admissionNo || "N/A"}`,
      ];

      const detailPositions = [
        { x: 20, y: detailsTopMargin },
        { x: 20, y: detailsTopMargin + 6 },
        { x: 102, y: detailsTopMargin },
        { x: 105, y: detailsTopMargin + 6 },
        { x: 193, y: detailsTopMargin },
        { x: 193, y: detailsTopMargin + 6 },
      ];

      studentDetails.forEach((detail, index) => {
        const position = detailPositions[index];
        doc.text(detail, position.x, position.y, {
          align: index >= 4 ? "right" : "left",
        });
      });

      const headers = [
        "SUBJECT",
        "DATE",
        "START TIME",
        "END TIME",
        "INVIGILATOR'S SIGN",
      ];

      const convertToDateFormat = (date) => {
        if (!date) return "N/A";
        const [year, month, day] = date.split("-").map(Number);
        return `${day.toString().padStart(2, "0")}/${month
          .toString()
          .padStart(2, "0")}/${year}`;
      };

      const convertTo12HourFormat = (time) => {
        if (!time) return "N/A";
        const [hours, minutes] = time.split(":").map(Number);
        const period = hours >= 12 ? "PM" : "AM";
        const adjustedHours = hours % 12 || 12;
        return `${adjustedHours}:${minutes.toString().padStart(2, "0")} ${period}`;
      };

      const tableData = apiData.data.map((entry) => [
        entry.subjectName || "N/A",
        convertToDateFormat(entry.firstExamDate),
        convertTo12HourFormat(entry.examStartTime),
        convertTo12HourFormat(entry.examEndTime),
        entry.remarks || "",
      ]);

      let tableTopMargin = 5;
      let startY = 74 + tableTopMargin;
      doc.setFontSize(10);
      doc.setTextColor(textColor);

      doc.setFillColor(headerColor);
      doc.setTextColor("#FFF");
      doc.rect(19.5, startY, 176, 10, "F");

      const columnWidths = [50, 30, 30, 30, 35];
      const tableStartX = 20;
      const tableHeaderHeight = 10;

      headers.forEach((header, index) => {
        const xPosition =
          tableStartX + columnWidths.slice(0, index).reduce((a, b) => a + b, 0);
        doc.text(header, xPosition + columnWidths[index] / 2, startY + 7, {
          align: "center",
        });
      });

      tableData.forEach((row, rowIndex) => {
        let currentX = tableStartX;
        const rowY = startY + (rowIndex + 1) * tableHeaderHeight;
        row.forEach((cell, cellIndex) => {
          const columnWidth = columnWidths[cellIndex];
          doc.rect(currentX, rowY, columnWidth, tableHeaderHeight);
          doc.setTextColor("#000");
          doc.text(cell, currentX + columnWidth / 2, rowY + 7, {
            align: "center",
          });
          currentX += columnWidth;
        });
      });

      const lineY = startY + 100;
      doc.setDrawColor("#000");
      doc.line(20, lineY - 5, 70, lineY - 5);
      doc.line(80, lineY - 5, 130, lineY - 5);
      doc.line(140, lineY - 5, 190, lineY - 5);

      doc.setFont("Helvetica", "normal");
      doc.text("CLASS TEACHER", 30, lineY);
      doc.text("CLERK", 100, lineY);
      doc.text("PRINCIPAL/DEAN", 150, lineY);

      const wrapText = (doc, text, x, y, maxWidth) => {
        const lines = doc.splitTextToSize(text, maxWidth);
        let currentY = y;
        lines.forEach((line) => {
          doc.text(line, x, currentY);
          currentY += 6;
        });
        return currentY;
      };

      const boxTopMargin = 40;
      const dividerY = lineY + boxTopMargin - 25;
      doc.setDrawColor("#000");
      doc.setLineWidth(0.5);
      doc.line(10, dividerY, 202, dividerY);

      doc.setLineWidth(0.2);

      const headingY = lineY + boxTopMargin - 6;
      doc.setFont("Helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor("#333");
      doc.text("Important Instructions:", 25, headingY);

      const boxTopY = lineY + boxTopMargin;
      const boxHeight = 60;
      doc.setDrawColor("#000");
      doc.rect(20, boxTopY, 170, boxHeight);

      const instructions = [
        "1. Students must carry this admit card to the examination hall.",
        "2. Ensure all details on this admit card are correct.",
        "3. Report to the examination hall 30 minutes before the start time.",
        "4. Follow all rules and instructions during the examination.",
        "5. Personal belongings such as bags, mobile phones, and electronic devices are not allowed in the examination hall.",
        "6. Students must sit in the assigned seat as per the roll number.",
        "7. Maintain silence during the exam and raise your hand if you need assistance from the invigilator.",
      ];

      doc.setFontSize(10);
      doc.setFont("Helvetica", "normal");
      doc.setTextColor("#000");

      let instructionY = boxTopY + 8;
      const maxWidth = 150;

      instructions.forEach((instruction) => {
        instructionY = wrapText(doc, instruction, 25, instructionY, maxWidth);
        if (instructionY > boxTopY + boxHeight - 8) {
          console.warn("Text is overflowing, consider increasing box height.");
        }
      });

      setLoadingBarProgress(100);
      const pdfBlob = doc.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank");
      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 5000);
    } catch (error) {
      console.error("Error generating PDF:", error);
      showSnackbar("Error generating admit card: " + error.message, "error");
    } finally {
      setIsPdfLoading(false);
      setLoadingBarProgress(0);
    }
  };

  return (
    <Box sx={{ p: 3, minHeight: "100vh" }}>
      <LoadingBar progress={loadingBarProgress} color="rgba(69,150,164,1)" height={5} />
      <Card sx={{ maxWidth: "100%", mx: "auto", boxShadow: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <AdmitCardIcon sx={{ fontSize: 40, color: "rgba(69,150,164,1)", mr: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: "bold", color: "rgba(17,20,34,1)" }}>
              Admit Card
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel id="exam-type-label">Exam Type</InputLabel>
                <Select
                  labelId="exam-type-label"
                  value={selectedExamType}
                  label="Exam Type"
                  onChange={(e) => setSelectedExamType(e.target.value)}
                >
                  {examTypes.map((type) => (
                    <MenuItem key={type.examCriteriaId} value={type.examCriteriaId}>
                      {type.examType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth disabled={isSubExamDisabled}>
                <InputLabel id="sub-exam-label">Sub Exam</InputLabel>
                <Select
                  labelId="sub-exam-label"
                  value={selectedSubExam}
                  label="Sub Exam"
                  onChange={(e) => setSelectedSubExam(e.target.value)}
                >
                  {subExams.map((subExam) => (
                    <MenuItem key={subExam.id} value={subExam.id}>
                      {subExam.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel id="session-label">Session</InputLabel>
                <Select
                  labelId="session-label"
                  value={session}
                  label="Session"
                  displayEmpty
                  onChange={(e) => setSession(e.target.value)}
                >
                  <MenuItem value="" disabled>
                    Select Session
                  </MenuItem>
                  {sessions.map((item) => (
                    <MenuItem key={item.financialYearID} value={item.financialYearID}>
                      {item.finanacialYear}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="center" mb={3}>
            <Button
              variant="contained"
              onClick={handleGetAdmitDetails}
              disabled={isLoading}
              sx={{
                backgroundColor: "#063552",
                "&:hover": { backgroundColor: "#0c5a8a" },
                minWidth: "180px",
              }}
            >
              {isLoading ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "GET ADMIT CARD"
              )}
            </Button>
          </Box>

          {hasSearched && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
                Admit Card Details
              </Typography>
              <Table sx={{ border: "1px solid #ccc", minWidth: 650 }}>
                <TableHead>
                  <TableRow
                    sx={{
                      background:
                        "linear-gradient(111.1deg, rgba(69,150,164,1) 2.5%, rgba(17,20,34,1) 100.3%)",
                    }}
                  >
                    <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>
                      Roll No
                    </TableCell>
                    <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>
                      Student Name
                    </TableCell>
                    <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>
                      Admission No
                    </TableCell>
                    <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>
                      Father's Name
                    </TableCell>
                    <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {studentAdmitData ? (
                    <TableRow
                      sx={{
                        "&:hover": { backgroundColor: "#f0f0f0" },
                      }}
                    >
                      <TableCell align="center">{studentAdmitData.rollNo || "N/A"}</TableCell>
                      <TableCell align="center">{studentAdmitData.studentName || "N/A"}</TableCell>
                      <TableCell align="center">{studentAdmitData.admissionNo || "N/A"}</TableCell>
                      <TableCell align="center">
                        {typeof studentAdmitData.fathersName === "string"
                          ? studentAdmitData.fathersName
                          : "N/A"}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Download Admit Card">
                          <span>
                            <Button
                              onClick={handleDownloadClick}
                              variant="contained"
                              disabled={isFeeCheckLoading}
                              sx={{
                                backgroundColor: "rgba(69,150,164,1)",
                                "&:hover": { backgroundColor: "rgba(17,20,34,1)" },
                                minWidth: "72px",
                              }}
                            >
                              {isFeeCheckLoading ? (
                                <CircularProgress size={16} sx={{ color: "#fff" }} />
                              ) : (
                                "Download"
                              )}
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No admit card found for the selected criteria.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Missing selection dialog ── */}
      <Dialog open={openMissingDialog} onClose={handleMissingDialogClose}>
        <DialogTitle>Missing Information</DialogTitle>
        <DialogContent>Please select Exam Type, Sub Exam and Session before proceeding.</DialogContent>
        <DialogActions>
          <Button onClick={handleMissingDialogClose} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Fee-due block — no bypass for students ── */}
      <Dialog
        open={dueDialogOpen}
        onClose={handleCloseDueDialog}
        maxWidth="sm"
        fullWidth
        sx={{ "& .MuiDialog-paper": { borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.2)" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#b22222",
            color: "#fff",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmberIcon /> Admit Card Unavailable
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <Typography variant="body1" sx={{ color: "red", fontWeight: "bold", mt: 1, mb: 1 }}>
            {dueData?.message || "The admit card cannot be generated because there are unpaid fee dues."}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            Total Due Amount: ₹{dueData?.totalDueAmount ?? 0}
          </Typography>

          {dueData?.dueMonths?.length > 0 && (
            <Table size="small" sx={{ border: "1px solid #eee" }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Total Fee</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Payment Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dueData.dueMonths.map((m, idx) => {
                  const isPaid = m.paidStatus === 1;
                  return (
                    <TableRow key={`${m.monthId}-${idx}`}>
                      <TableCell>{MONTH_NAMES[m.monthId] ?? m.monthId}</TableCell>
                      <TableCell align="right">{m.totalFee}</TableCell>
                      <TableCell align="right">{m.paidAmount}</TableCell>
                      <TableCell align="right">{m.balance}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={isPaid ? "Paid" : "Unpaid"}
                          size="small"
                          color={isPaid ? "success" : "error"}
                        />
                      </TableCell>
                      <TableCell align="center">{formatDueDate(m.feePaymentDate)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
            Please clear the pending dues to view or download the admit card.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button onClick={handleCloseDueDialog} variant="contained" color="error">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── PDF generation progress ── */}
      <Dialog
        open={isPdfLoading}
        PaperProps={{ style: { background: "transparent", boxShadow: "none", overflow: "hidden" } }}
        sx={{ "& .MuiDialog-container": { alignItems: "center", justifyContent: "center" } }}
      >
        <DialogContent
          sx={{
            background: "white",
            padding: "20px",
            borderRadius: "8px",
            textAlign: "center",
            minWidth: "200px",
          }}
        >
          <CircularProgress size={32} sx={{ mb: 1.5 }} />
          <Typography>Generating Admit Card, please wait...</Typography>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentAdmitCard;